// store/listings.js
import { defineStore } from 'pinia';
import sanctum from '@api/auth/sanctum';
import userAPI from '@api/user';
import meAPI from '@api/me';
import navigation from '@router/navigation';
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { buildCompanySettingsScopeKey } from '@/utilities/companySettingsScope';
import { useNotificationsStore } from './notifications';
import { useJobCountsStore } from './jobCounts';

/**
 * Helper to get current user roles.
 * Checks state.data.roles first, then falls back to current tenant's roles.
 * This handles the race condition where roles may not be set yet during initial load.
 */
function getCurrentRoles(state) {
    // If roles are already set on data, use them
    if (state.data?.roles?.length) {
        return state.data.roles;
    }
    // Fallback: get roles from current tenant
    if (state.tenantId && state.data?.tenants) {
        const currentTenant = state.data.tenants.find((t) => t.id === parseInt(state.tenantId));
        return currentTenant?.roles || [];
    }
    return [];
}

export const useUserStore = defineStore('user', {
    state: () => {
        return {
            loading: false,
            data: null,
            authenticated: false,
            search: null,
            navigation: [],
            bearer: useStorage('bearer', null),
            tenantId: useStorage('tenantId', null),
            tenantSlug: useStorage('tenantSlug', null),
            tenantModalStatus: false,
            isMoveproAdmin: false,
            tenantUserSelected: useStorage('tenantUserSelected', false),
            activity_status: useStorage('activity_status', null),
        };
    },
    actions: {
        async login(username, password) {
            try {
                this.loading = true;

                this.tenantId = null;
                this.tenantUserSelected = false;

                const { token } = await sanctum.login({
                    email: username,
                    password,
                });

                this.bearer = token;

                const user = await this.fetchUser();

                this.loading = false;

                return user;
            } catch (error) {
                this.authenticated = false;
                this.data = null;
                this.loading = false;

                // let the form component display the error
                throw error;
            }
        },

        async retrieve(userId) {
            return await userAPI.retrieve(userId);
        },

        async fetchUser(fetchNotifications = true) {
            try {
                this.loading = true;

                const { data } = await sanctum.me();

                this.data = data;

                if (data.tenants.length > 0) {
                    const isMultiTenantFreshLogin = data.tenants.length > 1 && !this.tenantUserSelected;
                    const shouldFetchSideEffects = fetchNotifications && !isMultiTenantFreshLogin;

                    // if a tenant has already been set, use it if it exists for this user
                    if (this.tenantId) {
                        const validatedTenant = this.data.tenants.find(
                            (tenant) => tenant.id === parseInt(this.tenantId),
                        );

                        if (validatedTenant) {
                            await this.setTenant(validatedTenant.id, shouldFetchSideEffects);
                        } else {
                            await this.setTenant(data.tenants[0].id, shouldFetchSideEffects);
                        }
                    } else {
                        // otherwise, use the first tenant available for this user as the default
                        await this.setTenant(data.tenants[0].id, shouldFetchSideEffects);
                    }
                }

                this.activity_status = data.activity_status;
                this.authenticated = true;
                this.loading = false;

                return data;
            } catch (error) {
                this.authenticated = false;
                this.data = null;
                this.loading = false;

                if (this.router && this.router.currentRoute.value.name !== 'Register') {
                    this.router.push('login');
                }
            }
        },

        async logout(silent = false) {
            try {
                this.loading = true;

                await sanctum.logout(silent);

                if (import.meta.env.VITE_ENV == 'production') {
                    Sentry.setUser(null);
                }

                this.data = null;
                this.authenticated = false;
                this.jwt = null;
                this.loading = false;

                this.tenantId = null;
                this.tenantSlug = null;
                this.tenantUserSelected = false;
            } catch (error) {
                this.data = null;
                this.authenticated = false;
                this.loading = false;

                throw error;
            } finally {
                this.loading = false;
            }
        },

        async update(userData) {
            try {
                const { data } = await userAPI.update(this.data.id, userData);
                this.data = data.user;

                toast.success('User updated successfully');

                return data.user;
            } catch (error) {
                toast.error('User update failed');

                throw error;
            }
        },

        async requestResetPassword(email) {
            const { data } = await userAPI.requestResetPassword(email);

            if (data) {
                return true;
            }
        },

        async confirmResetPassword(token, email, password, password_confirmation) {
            const { data } = await userAPI.confirmResetPassword(token, email, password, password_confirmation);

            if (data) {
                return true;
            }
        },

        async setTenant(tenantId, fetchNotifications = true) {
            const tenant = this.data.tenants.find((tenant) => tenant.id === tenantId);

            if (tenant) {
                try {
                    await meAPI.setActiveTenant(tenantId);
                } catch (error) {
                    console.error('Failed to set active tenant in session:', error);
                    throw error;
                }

                this.tenantId = tenant.id;
                this.tenantSlug = tenant.slug;
                this.isMoveproAdmin = this.tenantId == 1000;

                // Set user's roles from the selected tenant's roles
                // This ensures all role checks use the correct per-tenant roles
                this.data.roles = tenant.roles || [];
            }

            if (import.meta.env.VITE_ENV == 'production') {
                Sentry.setUser({
                    id: this.data.id,
                    name: this.data.fullname,
                    email: this.data.email,
                    tenant_id: this.tenantId,
                    tenant_slug: this.tenantSlug,
                });
            }

            const companyStore = useCompanyStore();
            const stripePaymentStore = useStripePaymentStore();
            const defaultCompanyId = tenant?.default_company_id ?? null;
            const activeSettingsScopeKey = buildCompanySettingsScopeKey(tenant?.id, tenant?.slug, defaultCompanyId);

            if (companyStore.settingsScopeKey !== activeSettingsScopeKey) {
                companyStore.$reset();
            }

            if (defaultCompanyId) {
                companyStore.setCompanyId(defaultCompanyId);

                if (tenant?.company) {
                    companyStore.data = {
                        id: defaultCompanyId,
                        ...tenant.company,
                    };
                    companyStore.settings = {
                        bank_institutions: [],
                        bank_details: null,
                        ...(tenant.company.settings || {}),
                    };
                }

                const settings = await companyStore.retrieveSettings(defaultCompanyId);
                if (settings?.stripe_id) {
                    await stripePaymentStore.initStripe(settings.stripe_id);
                }
            }

            // this.navigation = navigation.populateNavigation(this.isTenantContext, [...this.data.roles]);
            // Initial navigation build - will be updated by watcher
            this.rebuildNavigation(false); // Start with desktop view

            if (fetchNotifications && !this.isCentralContext) {
                try {
                    const notifications = useNotificationsStore();
                    await notifications.fetchUnreadCount();

                    const shouldFetchJobCounts = !this.isRestrictedFieldStaff();

                    if (shouldFetchJobCounts) {
                        const jobCounts = useJobCountsStore();
                        await jobCounts.fetchCounts();
                    }

                    if (window.echo) {
                        notifications.stopListener();

                        if (shouldFetchJobCounts) {
                            const jobCounts = useJobCountsStore();
                            jobCounts.stopListener();
                        }

                        window.echo.leaveAllChannels();

                        notifications.startListener();

                        if (shouldFetchJobCounts) {
                            const jobCounts = useJobCountsStore();
                            jobCounts.startListener();
                        }
                    } else {
                        console.log('Echo not available for WebSocket setup');
                    }
                } catch (_e) {
                    console.warn('Notification/job count setup failed:', _e?.message);
                }
            }
        },

        async showTenantModal() {
            // Refresh tenants list without triggering tenant switch
            await this.refreshTenants();
            this.tenantModalStatus = true;
        },

        async refreshTenants() {
            // Fetch fresh user data but don't call setTenant
            // This is used when opening the tenant modal to get updated tenant list
            try {
                const { data } = await sanctum.me();
                this.data.tenants = data.tenants;
                this.data.pending_invites_count = data.pending_invites_count ?? 0;
            } catch (error) {
                console.error('Failed to refresh tenants:', error);
            }
        },

        hideTenantModal() {
            this.tenantModalStatus = false;
        },

        toggleTenantModal() {
            this.tenantModalStatus = this.tenantModalStatus ? false : true;
        },

        rebuildNavigation(isMobile) {
            if (this.data && this.data.roles) {
                this.navigation = navigation.populateNavigation(this.isTenantContext, [...this.data.roles], isMobile);
            }
        },

        async resolvePostAuthRoute() {
            const tenants = this.data?.tenants || [];
            const pendingInvitesCount = this.data?.pending_invites_count || 0;

            // Previously selected tenant is still valid — go straight through
            if (this.tenantUserSelected && this.tenantId) {
                const isValid = tenants.some((t) => t.id === parseInt(this.tenantId));
                if (isValid) {
                    return { name: this.isRestrictedFieldStaff() ? 'Home' : 'Dashboard' };
                }
            }

            // Has admin tenant 1000 — auto-select it and go to dashboard
            if (tenants.some((t) => t.id === 1000)) {
                if (this.tenantId !== 1000) {
                    await this.setTenant(1000, true);
                }
                return { name: 'Dashboard' };
            }

            // Exactly 1 tenant and no pending invites — auto-select and go straight
            if (tenants.length === 1 && pendingInvitesCount === 0) {
                return { name: this.isRestrictedFieldStaff() ? 'Home' : 'Dashboard' };
            }

            // 0 or >1 total options (tenants + invites) without 1000 — tenant select screen
            return { name: 'SelectTenant' };
        },
    },
    getters: {
        companyHasStripeAccount: (state) => !!state.data?.company?.settings?.stripe_id,
        isAuthenticated: (state) => {
            return state.authenticated;
        },

        isCentralContext(state) {
            return state.data?.company_id === 1000 && state.tenantId === 1000 && state.tenantSlug === 'movepro';
        },

        isTenantContext() {
            return this.isCentralContext === false;
        },

        hasMultipleTenants(state) {
            return state.data?.tenants?.length > 1;
        },

        hasPendingInvites(state) {
            return (state.data?.pending_invites_count || 0) > 0;
        },

        isRemovalist(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('MANAGER');
        },

        isAdmin(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('ADMIN');
        },

        isOwner(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OWNER');
        },

        canSeeFinancialInformation(state) {
            const roles = getCurrentRoles(state);
            return roles.some((role) =>
                ['SUPER', 'OWNER', 'ADMIN', 'MANAGER', 'FINANCE', 'OPS', 'SALES'].includes(role),
            );
        },

        isFinance(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('FINANCE');
        },

        isAgent(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('SALES');
        },

        isTriage(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OPS');
        },

        isAdminOrSuper(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('ADMIN') || roles.includes('SUPER');
        },

        isOwnerOrSuper(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OWNER') || roles.includes('SUPER');
        },

        isManager(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('MANAGER');
        },

        isSuper(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('SUPER');
        },

        isDev(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('DEVELOPER');
        },

        // only applies when you're in the Movepro tenant view (i.e. when the active tenant is Movepro)
        isMoveproSuperAdminView(state) {
            return state.isMoveproAdmin && state.hasAnyRole(['SUPER', 'DEVELOPER']);
        },

        // applies regardless of tenant context (i.e. when you're a Movepro user in any tenant)
        isMoveproSuperAdminUser(state) {
            return state.data?.company_id === 1000;
        },

        // we should migrate usage of this to isMoveproSuperAdminView and remove this getter
        // we also need to audit use of this and .isMoveproAdmin in the codebase as it's ambiguous
        // and potentially used incorrectly or with incorrect assumptions about when it's truthy
        isMoveproSuperAdmin(state) {
            return state.isMoveproSuperAdminView;
        },

        companyId(state) {
            return state.data?.company_id;
        },

        isOnboarded(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OWNER') || roles.includes('ADMIN');
        },

        getUser(state) {
            return state.data;
        },

        getUserId(state) {
            return state.data?.id;
        },

        getTenant(state) {
            if (state.tenantId) {
                return state.data?.tenants?.find((tenant) => tenant.id === parseInt(state.tenantId, 10));
            }

            return null;
        },

        getTimezone(state) {
            return state.data?.timezone;
        },

        getAvatar(state) {
            return state.data?.avatar;
        },

        getFullName(state) {
            if (state.data && state.data.fullname) {
                return state.data.fullname;
            }
        },

        getHourlyRate(state) {
            return state.data?.hourly_rate ?? 0;
        },

        getInitials(state) {
            if (state.data) {
                const firstName = state.data.first_name || '';
                const lastName = state.data.last_name || '';

                const firstInitial = firstName.charAt(0).toUpperCase();
                const lastInitial = lastName.charAt(0).toUpperCase();

                return `${firstInitial}${lastInitial}`;
            }
            return '';
        },

        getFirstName(state) {
            if (state.data && state.data.first_name) {
                return state.data.first_name;
            }
        },

        getLastName(state) {
            if (state.data && state.data.last_name) {
                return state.data.last_name;
            }
        },

        getUsername(state) {
            if (state.data) {
                return state.data.username;
            }
        },

        getRoles(state) {
            return getCurrentRoles(state);
        },

        getCompanyId(state) {
            if (state.data) {
                return state.data.company_id;
            }
        },

        getTeam(state) {
            return state.data?.team;
        },

        // gates
        hasRole: (state) => {
            return (role) => {
                const roles = getCurrentRoles(state);
                return roles.includes(role);
            };
        },

        unlessRole: (state) => {
            return (role) => {
                const roles = getCurrentRoles(state);
                return !roles.includes(role);
            };
        },

        hasAnyRole: (state) => {
            return (roleList, alwaysAllowOwnerAndAdmin = true) => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                if (alwaysAllowOwnerAndAdmin && (roles.includes('OWNER') || roles.includes('ADMIN'))) {
                    return true;
                }

                const rolesToCheck = Array.isArray(roleList) ? roleList : [roleList];
                return rolesToCheck.some((r) => roles.includes(r));
            };
        },

        hasEveryRole: (state) => {
            return (rolesToCheck) => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                // Admin roles have access to everything
                if (roles.includes('OWNER') || roles.includes('ADMIN')) {
                    return true;
                }
                return rolesToCheck.every((role) => roles.includes(role));
            };
        },

        // Helper method to check if user has FIELD_STAFF or CONTRACTOR role but is restricted (no OWNER/ADMIN/MANAGER/OPS/SALES)
        isRestrictedFieldStaff: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                const hasFieldRole = roles.includes('FIELD_STAFF') || roles.includes('CONTRACTOR');
                const hasAdminRole =
                    roles.includes('OWNER') ||
                    roles.includes('ADMIN') ||
                    roles.includes('MANAGER') ||
                    roles.includes('OPS') ||
                    roles.includes('SALES');
                return hasFieldRole && !hasAdminRole;
            };
        },

        // Helper method to check if user has only FIELD_STAFF or CONTRACTOR role
        isOnlyFieldStaff: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                const fieldRoles = roles.filter((r) => r === 'FIELD_STAFF' || r === 'CONTRACTOR');
                return fieldRoles.length > 0 && fieldRoles.length === roles.length;
            };
        },

        // Helper method to check if user is a contractor (for timesheet restrictions)
        isContractor: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                return roles.includes('CONTRACTOR');
            };
        },

        // Helper method to check if user should see count badges in navigation
        shouldShowCountBadges: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                // Show counts only for roles that have access to orders/leads
                return roles.some((role) =>
                    ['SUPER', 'ADMIN', 'OWNER', 'MANAGER', 'FINANCE', 'SALES', 'OPS'].includes(role),
                );
            };
        },

        getBaseAccent: (state) => {
            if (state.tenantId) {
                const tenant = state.data?.tenants?.find((t) => t.id === parseInt(state.tenantId, 10));
                if (tenant?.is_branded_crm_enabled && tenant?.accent) {
                    return tenant.accent;
                }
            }

            return '#1F3DF0';
        },

        getTenantAccent: (state) => {
            if (state.tenantId) {
                return state.data?.tenants?.find((tenant) => tenant.id === parseInt(state.tenantId, 10))?.accent;
            }

            return '#1F3DF0';
        },

        getTenantLogo: (state) => {
            if (state.tenantId) {
                const tenant = state.data?.tenants?.find((t) => t.id === parseInt(state.tenantId, 10));
                if (tenant?.is_branded_crm_enabled && tenant?.logo) {
                    return tenant.logo;
                }
            }

            return null;
        },

        hasAuth() {
            return (rolesToCheck) => {
                return this.hasAnyRole(rolesToCheck, true);
            };
        },
    },
});
